import axios from "axios";
import store from "@/store";
import { DescQtdeMin, Produto, UnidadesMedida } from "../interfaces/Produto";

export type Pagination = {
  page: number;
  itemsPerPage: number;
  sortBy?: string[];
  sortDesc?: boolean[];
};

export type filters = {
  searchStr: string;
  incluirPrecos: boolean;
  subFiltros: subFiltros[];
};

export type subFiltros = {
  categoria: string;
  valores: {
    id: string;
    grupoId?: string;
  }[];
};

// Calcula o estoque com base na unidade de medida selecionada
export function calcular_estoque_diponivel_unidade_medida(data: { lista_unidade_medida: UnidadesMedida[]; estoque_disponivel: number; unidadeMedida: string; unidadeMedidaPadrao: string }) {
  const { lista_unidade_medida, estoque_disponivel, unidadeMedida, unidadeMedidaPadrao } = data;
  if (!lista_unidade_medida?.length) return estoque_disponivel;

  const un = lista_unidade_medida.find((r) => r.id == unidadeMedida);
  const unPadrao = lista_unidade_medida.find((r) => r.id == unidadeMedidaPadrao);

  if (un && unPadrao) {
    const estoque = (Number(un.fatorQtde || 0) * Number(unPadrao.fatorQtde || 0));
    return Math.floor(estoque_disponivel / estoque);
  }

  return Math.floor(estoque_disponivel);
}

// Retorna a quantidade de produtos com base na unidade de medida
export function calcular_quantidade_unidade_medida(data: { lista_unidade_medida: UnidadesMedida[]; quantidade: number; unidadeMedida: string; unidadeMedidaPadrao: string }) {
  const { lista_unidade_medida, quantidade, unidadeMedida, unidadeMedidaPadrao } = data;
  if (!lista_unidade_medida?.length) return quantidade;

  if (lista_unidade_medida?.length && unidadeMedida) {
    const un = lista_unidade_medida.find((r) => r.id == unidadeMedida);
    const unPadrao = lista_unidade_medida.find((r) => r.id == unidadeMedidaPadrao);
    const unPadraoFatorQtde = 1;

    // Se a unidade de medida for diferente da unidade de medida padrão do produto
    if (un && unPadrao) {
      const fatp = +un.fatorPreco > 0 ? +un.fatorPreco : 1;
      if (un?.id == unPadrao?.id) return +quantidade * unPadraoFatorQtde;
      return (+quantidade / unPadraoFatorQtde) * (+un.fatorQtde * fatp);
    }

    // se não tiver unidade de medida padrão no produto faz apenas a conversão
    if (un) {
      return +quantidade * +un.fatorQtde;
    }

    return +quantidade;
  }

  // se não tiver unidade de medida retorna a quantidade do produto

  return +quantidade;
}

// Retorna o preço unitário com base no desconto por quantidade mínima
export function calcular_preco_quantidade_minima(data: { lista_descontos: DescQtdeMin[]; quantidade: number; preco_unit: number }) {
  const { lista_descontos, quantidade, preco_unit } = data;
  if (!lista_descontos?.length) return preco_unit;

  const desconto = lista_descontos.reduce((acc, cur) => {
    if (quantidade >= +cur.qtdeMin) {
      return +cur.percDesconto;
    }
    return acc;
  }, 0);

  return preco_unit - preco_unit * (desconto / 100);
}

// Preço unitário com base na unidade de medida
export function calcular_preco_unidade_medida(data: { lista_unidade_medida: UnidadesMedida[]; preco_unit: number; unidadeMedida: string; unidadeMedidaPadrao: string }) {
  const { lista_unidade_medida, preco_unit, unidadeMedida, unidadeMedidaPadrao } = data;
  if (!lista_unidade_medida?.length) return preco_unit;

  if (lista_unidade_medida?.length && unidadeMedida) {
    const un = lista_unidade_medida.find((r) => r.id == unidadeMedida);
    const unPadrao = lista_unidade_medida.find((r) => r.id == unidadeMedidaPadrao);
    const unPadraoFatorQtde = 1;

    // Se a unidade de medida for diferente da unidade de medida padrão do produto
    if ((un != undefined) && (unPadrao != undefined)) {
      const fatp = +un.fatorPreco > 0 ? +un.fatorPreco : 1;
      if (un?.id == unPadrao?.id) return +preco_unit * unPadraoFatorQtde;
      return (+preco_unit / +unPadraoFatorQtde) * (+un.fatorQtde * fatp);
    }

    // se não tiver unidade de medida padrão no produto faz apenas a conversão
    if (un) {
      return +preco_unit * +un.fatorQtde;
    }

    return +preco_unit;
  }

  // se não tiver unidade de medida retorna o preço do produto

  return +preco_unit;
}

export function subFilterBuilder(filters: { categoria: string; id: string; grupoId?: string }[]) {
  return filters?.map((el) => {
    return {
      categoria: el.categoria,
      valores: filters
        .filter((el2) => el2.categoria === el.categoria)
        .map((val) => {
          if (el.categoria === 'Subgrupo') {
            return {
              id: val.id,
              grupoId: val.grupoId
            }
          }
          return { id: val.id };
        }),
    };
  });
}

export function produtosFilterBuilder(data: { search?: string; buscarPrecos?: boolean; filtros?: any[]; grupoProdutos?: number }) {
  const { filtros, search, buscarPrecos, grupoProdutos } = data;

  const filtroProdutos = {
    searchStr: search,
    incluirPrecos: buscarPrecos,
    subFiltros: subFilterBuilder(filtros || []),
    grupoProdutos: grupoProdutos
  }

  return filtroProdutos;
}

class ProdutosService {
  listaOrdenacaoMap = {
    MENOR_PRECO: { label: "Menor Preço", ord: { sortBy: ["prc.precoUnit"] } },
    MAIOR_PRECO: {
      label: "Maior Preço",
      ord: { sortBy: ["prc.precoUnit"], sortDesc: ["prc.precoUnit"] },
    },
    AZ: { label: "A-Z", ord: { sortBy: ["produto.descricao_portal"] } },
    ZA: {
      label: "Z-A",
      ord: { sortBy: ["produto.descricao_portal"], sortDesc: ["produto.descricao_portal"] },
    },
  };

  findAllCliente(data: { paginacao: Pagination; searchStr: string; filters: { categoria: string; id: string; grupoId?: string }[]; grupoProdutos?: number }): Promise<{ data: Produto[]; totalrecordcount: number }> {
    const isLoggedIn = Boolean(store.getters["Auth/isLoggedIn"]);

    if (isLoggedIn) {
      const filtroProdutos = produtosFilterBuilder({ ...data, buscarPrecos: true, search: data.searchStr, filtros: data.filters, grupoProdutos: data.grupoProdutos });

      return axios
        .get("produto/cliente", {
          headers: {
            pagination: JSON.stringify(data.paginacao),
            filter: JSON.stringify(filtroProdutos),
          }
        })
        .then((response) => {
          return {
            data: response.data?.data,
            totalrecordcount: +response.data?.TotalRecordCount
          };
        });
    }

    return this.findAllPublic({ ...data, search: data.searchStr, filtros: data.filters, grupoProdutos: data.grupoProdutos });
  }

  findAllPublic(data: { paginacao: any; search?: string; incluirFiltros?: boolean; buscarPrecos?: boolean; filtros?: any[]; grupoProdutos?: number }): Promise<{ data: Produto[]; totalrecordcount: number }> {
    const filtro = produtosFilterBuilder(data);
    return axios
      .get("produto/public", {
        headers: {
          pagination: JSON.stringify(data.paginacao),
          filter: JSON.stringify(filtro),
        }
      })
      .then((response) => {
        return {
          data: response.data?.data,
          totalrecordcount: +response.data?.TotalRecordCount
        };
      });
  }

  findAll(data: { paginacao: Pagination; search: string; incluirFiltros?: boolean; buscarPrecos?: boolean; filtros: any[] }): Promise<{ data: Produto[]; totalrecordcount: number }> {
    const filtro = produtosFilterBuilder(data);

    return axios
      .get("produto/all", {
        headers: {
          pagination: JSON.stringify(data.paginacao),
          filter: JSON.stringify(filtro)
        }
      })
      .then((response) => {
        return {
          data: response.data?.data,
          totalrecordcount: +response.data?.TotalRecordCount
        };
      });
  }

  update(data: Produto): Promise<Produto> {
    return axios.patch("produto", data).then((response) => {
      return response.data;
    });
  }

  getCategorias(): Promise<any> {
    const isLoggedIn = Boolean(store.getters["Auth/isLoggedIn"]);

    if (isLoggedIn) {
      return axios.get("produto/categoriasUser");
    }

    return axios.get("produto/categorias");
  }

  buscarProdutosPorId(lista: Array<any>) {
    lista = lista.map((el) => {
      return el.replace(/[/]/g, "%2F");
    });
    return axios.get("produto/ids/" + lista.join(","));
  }
}
export default new ProdutosService();
